import { AxiosError } from 'axios'
import epsClient from 'enrollment-platform/api/eps.client'
import {
  type DownloadableSurecoCsvDataType,
  type SurecoCsvDataType,
} from '../constants'

export async function downloadSurecoCsv(params: {
  taskId: string
  dataType: DownloadableSurecoCsvDataType
  year: number
  states: string[]
}): Promise<void> {
  const response = await epsClient.get(
    '/api/v1/plan-management-v2/tasks/sureco-csv/download',
    {
      params: {
        taskId: params.taskId,
        dataType: params.dataType,
        ...(params.states.length ? { states: params.states.join(',') } : {}),
      },
      responseType: 'blob',
    }
  )

  const filename = extractFilename(
    response.headers?.['content-disposition'],
    `${params.dataType}-${params.year}.csv`
  )

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function extractFilename(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return match?.[1] ?? fallback
}

export interface DbAvailablePlanYearsResponseDto {
  planYears: number[]
}

export async function getDbAvailablePlanYears(): Promise<number[]> {
  const { data } = await epsClient.get<DbAvailablePlanYearsResponseDto>(
    '/api/v1/plan-management-v2/tasks/db-available-years'
  )
  return data.planYears
}

export async function exportPlanData(
  params: {
    year: number
    dataType: SurecoCsvDataType
    states: string[]
  },
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  const response = await epsClient.get(
    '/api/v1/plan-management-v2/tasks/plan-data/export',
    {
      params: {
        dataType: params.dataType,
        year: params.year,
        ...(params.states.length ? { states: params.states.join(',') } : {}),
      },
      responseType: 'blob',
      onDownloadProgress: onProgress
        ? (progressEvent) => onProgress(progressEvent.loaded)
        : undefined,
    }
  )

  const filename = extractFilename(
    response.headers?.['content-disposition'],
    `${params.dataType}-${params.year}.csv`
  )

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

async function downloadCsvFromRoute(
  route: string,
  year: number,
  fallbackFilename: string,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  const response = await epsClient.get(route, {
    params: { year },
    responseType: 'blob',
    onDownloadProgress: onProgress
      ? (progressEvent) => onProgress(progressEvent.loaded)
      : undefined,
  })

  const filename = extractFilename(
    response.headers?.['content-disposition'],
    fallbackFilename
  )

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export async function exportPlanChangeImpacts(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/plan-change-impacts/export',
    year,
    `plan-change-impacts-${year}.csv`,
    onProgress
  )
}

export async function exportServiceAreaMismatchReport(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/service-area-mismatch/export',
    year,
    `service-area-mismatch-${year}.csv`,
    onProgress
  )
}

export async function exportMissingRatingAreaPriceReport(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/missing-rating-area-price/export',
    year,
    `missing-rating-area-price-${year}.csv`,
    onProgress
  )
}

export async function exportMissingBenefitReport(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/missing-benefit/export',
    year,
    `missing-benefit-${year}.csv`,
    onProgress
  )
}

export async function exportMissingSbcReport(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/missing-sbc/export',
    year,
    `missing-sbc-${year}.csv`,
    onProgress
  )
}

export async function exportCountyMismatchReport(
  year: number,
  onProgress?: (bytesLoaded: number) => void
): Promise<void> {
  return downloadCsvFromRoute(
    '/api/v1/plan-management-v2/health-plans/county-mismatch/export',
    year,
    `county-mismatch-${year}.csv`,
    onProgress
  )
}

export async function parseBlobError(err: unknown): Promise<string | null> {
  const parsed = await parseAnalyzeError(err)
  return parsed?.message ?? null
}

export type AnalyzeErrorDetails = {
  message: string
}

export async function parseAnalyzeError(
  err: unknown
): Promise<AnalyzeErrorDetails | null> {
  const axiosError = err as AxiosError
  const data = axiosError?.response?.data
  if (!data) return null

  if (data instanceof Blob && data.type.startsWith('application/json')) {
    try {
      const text = await data.text()
      const parsed = JSON.parse(text) as { message?: string }
      if (!parsed.message) return null
      return { message: parsed.message }
    } catch {
      return null
    }
  }

  if (typeof data === 'object' && 'message' in data) {
    const parsed = data as { message?: string }
    if (!parsed.message) return null
    return { message: parsed.message }
  }

  return null
}
